import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Avatar, Button, Collapse, Form, notification, Select, Tag } from "antd";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import { DownOutlined, LeftOutlined, TeamOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { QueryKeys, SystemContext } from "@Constants";
import { hasCapability } from "@Utils";
import UserService from "@Services/UserService";
import UserGroupService from "@Services/UserGroupService";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import { MainContext } from "@Context/provider";
import { useGroupAddMembers } from "@Components/groups/GroupUtils";

interface IGroupUsers {
    users: string[];
}

interface IUserValue {
    label: string;
    value: string;
}

interface IProps {
    groupId: string;
}

const ModuleAddGroupUsers: React.FC<IProps> = ({ groupId }: IProps) => {
    const queryClient = useQueryClient();
    const { Panel } = Collapse;
    const [value, setValue] = useState<IUserValue[]>([]);
    const [fetching, setFetching] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [userOptions, setUserOptions] = useState<IUserValue[]>([]);
    const { ui: uiState } = useContext(MainContext);
    const [data, setData] = useState<IUserGroupDetails>(null);
    const stateRef = useRef<IUserGroupDetails>();
    stateRef.current = data;

    const hasSystemCapability = hasCapability("UserGroup/AddMembers", SystemContext);

    /***
     * Search the entire system for users with matching names
     * @param username
     */
    const searchUsers = async (username: string) => {
        if (username.length < 3) return null;
        const userService = new UserService();
        setFetching(true);

        if (hasSystemCapability) {
            await userService.searchSystem(username).then(async (result) => {
                setFetching(false);
                if (result !== null && !result.hasErrors) {
                    let existing: string[] = [];
                    const service = new UserGroupService();
                    const group = await service.getMembers([groupId], -1);
                    if (!group.hasErrors) {
                        existing = group.value.users.map((x) => x.userId);
                    }
                    const newOptions = result.value.users
                        .filter((x) => !existing.includes(x.userId))
                        .map((x) => {
                            return {
                                label: x.fullName,
                                value: x.userId,
                            };
                        });
                    setUserOptions(newOptions);
                }
            });
        } else {
            await userService.searchGroup(groupId, username).then(async (result) => {
                setFetching(false);
                if (result !== null && !result.hasErrors) {
                    let existing: string[] = [];
                    const service = new UserGroupService();
                    const group = await service.getMembers([groupId], -1);
                    if (!group.hasErrors) {
                        existing = group.value.users.map((x) => x.userId);
                    }
                    const newOptions = result.value.users
                        .filter((x) => !existing.includes(x.userId))
                        .map((x) => {
                            return {
                                label: x.fullName,
                                value: x.userId,
                            };
                        });
                    setUserOptions(newOptions);
                }
            });
        }
    };

    /**
     * Return a user tag to be rendered in the Select box.
     * @param props
     */
    const renderUserTag = (props: CustomTagProps) => {
        const { label, closable, onClose } = props;
        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };
        return (
            <Tag
                color={"blue"}
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
            >
                {label}
            </Tag>
        );
    };

    /**
     * Add group members mutation
     */
    const groupAddMembers = useGroupAddMembers(groupId);

    /**
     * Add the submitted users to the current group.
     * @param addUsers
     */
    const onFinish = (addUsers: IGroupUsers) => {
        if (addUsers.users.length > 0) {
            groupAddMembers.mutate(addUsers.users, {
                onSuccess: async (res) => {
                    if (res.hasErrors) {
                        if (data?.capacity > 0) {
                            notification.error({ message: "This would exceed license capacity." });
                        } else {
                            notification.error({ message: "Error adding users." });
                        }
                    }
                },
            });
        }
        form.resetFields();
        setUserOptions(null);
    };

    /**
     * Fetch current group data.
     */
    useEffect(() => {
        stateRef.current = null;
        setData(null);

        const data = queryClient.getQueryData<IUserGroupDetails[]>([QueryKeys.UserGroups]);
        const group = data?.find((x) => x.groupId === groupId);
        if (group !== null) {
            stateRef.current = group;
            if (stateRef.current !== null) {
                setData(stateRef.current);
            }
        }
    }, [uiState.currentGroup]);

    return (
        data !== null && (
            <>
                <Collapse
                    destroyInactivePanel={false}
                    bordered={false}
                    ghost
                    expandIconPosition={"end"}
                    expandIcon={({ isActive }) => (
                        <Avatar
                            size="small"
                            className={"collapse-icon"}
                            icon={isActive ? <DownOutlined /> : <LeftOutlined />}
                        />
                    )}
                >
                    <Panel
                        header={
                            <>
                                <InspectorModuleTitle
                                    title={"Add Group Members"}
                                    slug={"add-group-members"}
                                    fetching={fetching}
                                    icon={<TeamOutlined />}
                                />
                                {data?.capacity > 0 && data?.count >= data?.capacity && (
                                    <Alert showIcon={true} type={"info"} message={"Capacity reached"} />
                                )}
                            </>
                        }
                        key={1}
                    >
                        <Form
                            form={form}
                            name="add-group-member"
                            className="inspector-form"
                            onFinish={(values: IGroupUsers) => {
                                onFinish(values);
                            }}
                            autoComplete="off"
                        >
                            <Form.Item
                                label="Users"
                                name="users"
                                rules={[{ required: true, message: "Please select at least one user" }]}
                            >
                                <Select
                                    placeholder={"Search"}
                                    filterOption={true}
                                    optionFilterProp="label"
                                    disabled={
                                        groupAddMembers.isLoading ||
                                        (data?.capacity > 0 && data?.count >= data?.capacity)
                                    }
                                    showArrow
                                    mode="multiple"
                                    value={value}
                                    tagRender={renderUserTag}
                                    style={{ width: "100%" }}
                                    options={userOptions}
                                    onSearch={AwesomeDebouncePromise(searchUsers, 800)}
                                    onChange={(newValue) => {
                                        setValue(newValue as IUserValue[]);
                                    }}
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    className="secondary"
                                    htmlType="submit"
                                    name="submit"
                                    disabled={
                                        groupAddMembers.isLoading ||
                                        (data?.capacity > 0 && data?.count >= data?.capacity)
                                    }
                                    loading={groupAddMembers.isLoading}
                                >
                                    Add
                                </Button>
                            </Form.Item>
                        </Form>
                    </Panel>
                </Collapse>
                <hr />
            </>
        )
    );
};
export default ModuleAddGroupUsers;
